import { useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateTask } from "../../../services/api/task";

import SignOffTab from "../Tabs/SignOffTab";
import { uploadSignOffSheet, updateWorkOrderStatus } from "../../../services/api/workOrder";

const MarkCompleteModal = ({
  markCompleteModalOpen,
  setMarkCompleteModalOpen,
  tasks,
}) => {
  const { updateWorkOrder } = useUpdateWorkOrder();
  const [reason, setReason] = useState("");
  const [signOffSheetData, setSignOffSheetData] = useState({});
  const dispatch = useDispatch();
  const workOrderDetail = useSelector(
    (state) => state.workOrder.workOrderDetails,
  );
  const user = useSelector((state) => state.authUser.value.user);
  const isCustomer = user?.role?.role === "CUSTOMER";
  const isWorkOrderIncomplete = workOrderDetail?.status === "INCOMPLETE";
  const {
    isPending: isUploadSignOffSheetApiLoading,
    error: isUploadSignOffSheetApiError,
    data: uploadedSignOffSheetData,
    mutateAsync: uploadSignOffSheetMutation,
  } = useMutation({
    mutationFn: uploadSignOffSheet,
    enabled: false,
  });

  const {
    isLoading: isTaskUpdateLoading,
    error: isTaskUpdateError,
    data: updatedTaskData,
    mutateAsync: updateTaskMutation,
  } = useMutation({
    mutationFn: updateTask,
    enabled: false,
  });

  const {
    isPending: isUpdateWorkOrderStatusLoading,
    mutateAsync: updateWorkOrderStatusMutation,
  } = useMutation({
    mutationFn: updateWorkOrderStatus,
    enabled: false,
  });

  const isSubmitDisabled = !signOffSheetData.engineerSignatureFile;

  const handleMarkComplete = async () => {
    // Customer confirmation flow ONLY for CUSTOMER userType with INCOMPLETE status
    // All other user types (CLIENT, FREELANCER, ADMIN, MODERATOR) continue with sign-off sheet flow
    if (isCustomer && isWorkOrderIncomplete) {
      try {
        await updateWorkOrderStatusMutation({
          id: workOrderDetail.id,
          status: "WORKDONE",
        });
        dispatch(
          showToast({
            message: "Work order marked as complete",
            type: "success",
          }),
        );
        setMarkCompleteModalOpen(false);
        updateWorkOrder();
      } catch (error) {
        dispatch(
          showToast({
            message: error?.response?.data?.message || "Failed to mark as complete",
            type: "error",
          }),
        );
      }
      return;
    }

    // Sign-off sheet flow for CLIENT, FREELANCER, ADMIN, MODERATOR, and CUSTOMER on non-INCOMPLETE work orders
    const formData = new FormData();
    const task = tasks?.find((task) => task.taskType === "MARK_COMPLETE");

    try {
      let payload = {
        id: task.completedTask?.id,
        // isCompleted,
        // text: reason,
      };

      const appendIfPresent = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      appendIfPresent("workOrderId", signOffSheetData?.workOrderId);
      appendIfPresent("clientName", signOffSheetData?.clientName);
      appendIfPresent("clientDesignation", signOffSheetData?.clientDesignation);
      appendIfPresent("clientSignatureFile", signOffSheetData?.clientSignatureFile);
      appendIfPresent("physicalSheetFile", signOffSheetData?.physicalSheetFile);
      appendIfPresent("clientId", signOffSheetData?.clientId);
      appendIfPresent("clientComment", signOffSheetData?.clientComment);
      appendIfPresent("engineerComment", signOffSheetData?.engineerComment);
      appendIfPresent("engineerSignatureFile", signOffSheetData?.engineerSignatureFile);
      appendIfPresent("engineerId", signOffSheetData?.engineerId);

      const signOffSheet = await uploadSignOffSheetMutation(formData);
      const updatedTask = await updateTaskMutation(payload);
      setMarkCompleteModalOpen(false);
      updateWorkOrder();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  // ONLY show confirmation modal for CUSTOMER userType with INCOMPLETE work order status
  // All other user types and scenarios continue with sign-off sheet flow
  if (isCustomer && isWorkOrderIncomplete) {
    return (
      <BaseConfirmModal
        open={markCompleteModalOpen}
        onClose={() => setMarkCompleteModalOpen(false)}
        onConfirm={handleMarkComplete}
        title="Confirm Work Order Completion"
        description={`Are you sure you want to mark work order ${workOrderDetail?.referenceId} (${workOrderDetail?.title}) as complete?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={isUpdateWorkOrderStatusLoading}
        type="confirm"
      />
    );
  }

  // Sign-off sheet flow for all other cases (CLIENT, FREELANCER, ADMIN, MODERATOR, etc.)
  return (
    <BaseModal
      open={markCompleteModalOpen}
      close={() => setMarkCompleteModalOpen(false)}
    >
      <div className="flex flex-col bg-gray-100 rounded-xl max-h-[80vh] overflow-hidden">
        <div className="sticky top-0 bg-white z-10 border-b p-4">
          <h6 className="text-dark mb-0">Sign-off Sheet</h6>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="container xl:w-[1500px] lg:w-[800px] w-[400px]">
            <SignOffTab
              setSignOffSheetData={setSignOffSheetData}
              mode="WRITE"
              workOrderDetail={workOrderDetail}
            />
          </div>
        </div>

        <div
          className="sticky bottom-0 flex items-center justify-center bg-white py-6 px-5 border-t z-10"
          style={{
            boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className="py-[5px] px-2 bg-[#E8E8E8] text-dark"
            fullWidth={false}
            onClick={() => setMarkCompleteModalOpen(false)}
          >
            Cancel
          </BaseButton>
          <BaseButton
            loaderSize="24px"
            disabled={isUploadSignOffSheetApiLoading || isSubmitDisabled}
            isLoading={isUploadSignOffSheetApiLoading}
            className="py-[5px] px-[28px] ml-4"
            color="primary"
            fullWidth={false}
            onClick={handleMarkComplete}
          >
            Submit
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default MarkCompleteModal;
